import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router';

/**
 * @component RouteMetaContent
 * @desc SEO 메타데이터를 렌더링하는 컴포넌트
 *
 * 사용 목적:
 * - 동적 데이터 기반 메타 태그 (예: 공지사항 상세, 게시글 상세 등)
 * - API 응답 데이터를 메타 태그에 포함해야 하는 경우
 *
 * @param {Object} value - SEO 설정 값 (타이틀, 설명, 키워드, Open Graph 및 Twitter 메타데이터 등)
 */
const RouteMetaContent = ({ value }) => {
  const location = useLocation();

  const {
    title,
    description,
    keywords,
    itemprop,
    og,
    twitter,
    schema,
    path = '',
  } = value || {};

  // 타이틀 포맷팅: INAVI Maps가 포함되어 있지 않으면 접미사 추가
  const formatTitle = (t) => {
    if (!t) return 'INAVI Maps';
    if (t.includes('INAVI Maps') || t.includes('INAVI Maps Platform Service'))
      return t;
    return `${t} | INAVI Maps`;
  };

  const displayTitle = formatTitle(title);

  const domain =
    process.env.REACT_APP_SITE_URL || 'https://mapsapi.inavisys.com';
  // imageUrl은 반드시 절대 경로여야 소셜 미디어 공유 시 정상 표시됨
  const imageUrl = `${domain}/inavi_maps_preview.png`;


  return (
    <Helmet>
      {/* 웹 페이지의 타이틀 설정 */}
      <title>{displayTitle}</title>

      {/* 모바일 웹앱용 설정 */}
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />

      {/* 기본 메타 데이터 */}
      <meta name="title" content={displayTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="아이나비시스템즈" />
      <link rel="canonical" href={domain + path} />
      <meta name="robots" content="index, follow" />
      {/* 마지막 업데이트 일자를 정적 렌더링 시점으로 고정하여 date 인식을 지원 */}
      <meta
        property="article:published_time"
        content="2025-02-01T00:00:00+09:00"
      />
      <meta
        property="article:modified_time"
        content={new Date().toISOString().split('T')[0]}
      />

      {/* itemprop 메타데이터 (구조화된 데이터) - Microdata/RDFa 대체 */}
      {itemprop && (
        <>
          <meta itemProp="name" content={itemprop.name} />
          <meta itemProp="description" content={itemprop.description} />
          <meta itemProp="image" content={imageUrl} />
        </>
      )}

      {/* Open Graph 메타 데이터 (소셜 미디어 공유 시 표시될 내용) */}
      {og && (
        <>
          <meta property="og:type" content="website" />
          <meta property="og:title" content={formatTitle(og.title)} />
          <meta property="og:site_name" content={og.site_name} />
          <meta property="og:description" content={og.description} />
          <meta property="og:image" content={imageUrl} />
          <meta property="og:url" content={domain + path} />
        </>
      )}

      {/* Twitter 카드 메타 데이터 (트위터에서 공유될 때 표시될 내용) */}
      {twitter && (
        <>
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={formatTitle(twitter.title)} />
          <meta name="twitter:description" content={twitter.description} />
          <meta name="twitter:image" content={imageUrl} />
        </>
      )}

      {/* JSON-LD Schema 삽입: 배열인 경우 @graph로 묶어서 단일 script 태그로 출력 (측정 도구 호환성 향상) */}
      <script type="application/ld+json">
        {schema
          ? JSON.stringify(
              Array.isArray(schema)
                ? {
                    '@context': 'https://schema.org',
                    '@graph': schema,
                  }
                : schema
            )
          : JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'WebPage',
              name: displayTitle,
              description:
                description ||
                '위치기반 서비스(LBS) 솔루션 전문 기업 아이나비시스템즈가 제공하는 통합 지도 API, 검색 API, 탐색 API 플랫폼입니다.',
              url: domain + (path || location.pathname),
              datePublished: '2025-02-01T00:00:00+09:00',
              dateModified: new Date().toISOString().split('T')[0],
              author: {
                '@type': 'Organization',
                name: '아이나비시스템즈',
              },
            })}
      </script>
    </Helmet>
  );
};

export default RouteMetaContent;
